import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../lib/auth';

export const Route = createFileRoute('/admin_/onboarding')({
  component: OnboardingPage,
});

const TOTAL_STEPS = 4;

function OnboardingPage() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [settings, setSettings] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);

  if (isLoading) return <Shell><p className="text-center pt-20 text-oud-500">…</p></Shell>;
  if (!user) {
    void navigate({ to: '/admin' });
    return null;
  }

  function update(key: string, value: string) {
    setSettings((s) => ({ ...s, [key]: value }));
  }

  async function saveAndNext() {
    setBusy(true);
    try {
      const updates = { ...settings };
      if (step === TOTAL_STEPS - 1) {
        updates['onboarding.completed'] = true;
      }
      await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(updates),
        credentials: 'include',
      });
      if (step < TOTAL_STEPS - 1) {
        setStep((s) => s + 1);
      } else {
        void navigate({ to: '/admin' });
      }
    } finally {
      setBusy(false);
    }
  }

  const steps = [
    <StepBrand key="brand" settings={settings} update={update} />,
    <StepContact key="contact" settings={settings} update={update} />,
    <StepPricing key="pricing" settings={settings} update={update} />,
    <StepDone key="done" />,
  ];

  return (
    <Shell>
      <div className="max-w-md mx-auto py-8 space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-black text-oud-900">إعداد صفحتك</h1>
          <p className="text-sm text-oud-600">الخطوة {step + 1} من {TOTAL_STEPS}</p>
        </div>

        {/* Progress bar */}
        <div className="flex gap-2">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div key={i} className={`flex-1 h-1.5 rounded-full transition-colors ${i <= step ? 'bg-gold' : 'bg-oud-100'}`} />
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
          >
            {steps[step]}
          </motion.div>
        </AnimatePresence>

        <div className="flex gap-3">
          {step > 0 ? (
            <button type="button" onClick={() => setStep((s) => s - 1)}
              className="flex-1 bg-cream border border-oud-100 text-oud-700 rounded-2xl py-3 font-bold hover:bg-oud-50 transition-colors">
              رجوع
            </button>
          ) : null}
          <button type="button" onClick={saveAndNext} disabled={busy}
            className="flex-1 bg-oud-800 text-sand rounded-2xl py-3 font-bold disabled:opacity-40 hover:bg-oud-900 transition-colors">
            {busy ? '…' : step === TOTAL_STEPS - 1 ? 'ابدأ الآن' : 'التالي'}
          </button>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="min-h-screen bg-sand text-oud-900 px-5" dir="rtl">{children}</div>;
}

function Field({ label, value, onChange, placeholder, dir }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; dir?: string;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-bold text-oud-800">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        dir={dir}
        className="w-full bg-cream border border-oud-100 rounded-xl px-4 py-3 text-base text-oud-900 placeholder-oud-300 focus:outline-none focus:border-gold"
      />
    </label>
  );
}

function StepBrand({ settings, update }: { settings: Record<string, unknown>; update: (k: string, v: string) => void }) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-black text-oud-900">العلامة التجارية</h2>
      <Field label="اسم النشاط (عربي)" value={String(settings['brand.name'] ?? '')} onChange={(v) => update('brand.name', v)} placeholder="سنديان" />
      <Field label="اسم النشاط (إنجليزي)" value={String(settings['brand.nameEn'] ?? '')} onChange={(v) => update('brand.nameEn', v)} placeholder="Sindian" />
    </div>
  );
}

function StepContact({ settings, update }: { settings: Record<string, unknown>; update: (k: string, v: string) => void }) {
  const phone = String(settings['contact.whatsappNumber'] ?? '');
  const validPhone = !phone || /^(\+?966|0)5\d{8}$/.test(phone);
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-black text-oud-900">التواصل</h2>
      <div>
        <Field label="رقم واتساب" value={phone} onChange={(v) => update('contact.whatsappNumber', v.replace(/[^\d+]/g, ''))} placeholder="05xxxxxxxx" />
        {phone && !validPhone ? (
          <p className="text-xs text-red-700 mt-1">رقم جوال سعودي غير صحيح (مثال: 05xxxxxxxx)</p>
        ) : null}
      </div>
      <Field label="المدينة" value={String(settings['delivery.city'] ?? '')} onChange={(v) => update('delivery.city', v)} placeholder="الدمام" />
    </div>
  );
}

function StepPricing({ settings, update }: { settings: Record<string, unknown>; update: (k: string, v: string) => void }) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-black text-oud-900">التسعير</h2>
      <p className="text-sm text-oud-600">يمكنك تعديل هذه الإعدادات والعديد من خيارات التخصيص الأخرى لاحقًا من لوحة التحكم</p>
      <PercentSlider
        label="الحد الأقصى للنسبة أقل من المتوسط"
        value={Number(settings['pricing.midpointFloorPct'] ?? 35)}
        onChange={(v) => update('pricing.midpointFloorPct', String(v))}
        hint="السعر لا ينزل أكثر من هذه النسبة تحت متوسط السوق"
      />
      <Field label="أقل سعر ممكن (ر.س)" value={String(settings['pricing.floorSAR'] ?? '500')} onChange={(v) => update('pricing.floorSAR', v)} />
    </div>
  );
}

function StepDone() {
  const slug = window.location.hostname.split('.')[0] ?? '';
  const storeUrl = window.location.origin;

  return (
    <div className="text-center space-y-5 py-6">
      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold to-oud-700 flex items-center justify-center mx-auto shadow-quote">
        <svg viewBox="0 0 24 24" fill="none" stroke="#fbf7f2" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8" aria-hidden>
          <path d="M5 12l5 5L20 7" />
        </svg>
      </div>
      <h2 className="text-xl font-black text-oud-900">صفحتك جاهزة!</h2>
      <p className="text-oud-600">شارك الرابط مع عملائك وابدأ باستقبال الطلبات</p>
      <div className="bg-cream border border-oud-100 rounded-2xl p-4 space-y-2">
        <p className="text-xs text-oud-500 font-bold">رابط صفحتك</p>
        <p className="text-lg font-black text-oud-900 break-all">{storeUrl}</p>
        <button
          type="button"
          onClick={() => { void navigator.clipboard.writeText(storeUrl); }}
          className="text-xs text-gold-deep font-bold hover:text-oud-900 transition-colors"
        >
          نسخ الرابط
        </button>
      </div>
      <p className="text-sm text-oud-500">اضغط "ابدأ الآن" للدخول إلى لوحة التحكم</p>
    </div>
  );
}

function PercentSlider({ label, value, onChange, hint }: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  hint?: string;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-bold text-oud-800">{label}</span>
        <span className="text-lg font-black text-oud-900 tabular-nums">{value}٪</span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 rounded-full appearance-none bg-oud-100 accent-gold cursor-pointer touch-none"
      />
      <div className="flex justify-between text-[10px] text-oud-400">
        <span>٠٪</span>
        <span>١٠٠٪</span>
      </div>
      {hint ? <p className="text-xs text-oud-500">{hint}</p> : null}
    </div>
  );
}
